import React, { FC, useMemo } from 'react';
import { Box, Paper, Tooltip } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Control, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import NumberField from 'components/number-field';
import { TitleWithValue } from 'components/title-with-value';
import { formatMoney } from 'utils/helpers';
import { ChargeFormEntry } from 'views/orders/charges/components/GeneralChargesTable/types';
import { getTotalRow } from 'views/orders/charges/components/GeneralChargesTable/components/TotalTableFooter/utils';

const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  numberField: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.background.default,
      color: theme.palette.primary.dark,
      borderRadius: '5px',
      fontSize: '13px',
      display: 'flex',
      justifyContent: 'flex-end',

      '& .MuiOutlinedInput-notchedOutline': {
        border: 'unset',
      },
      '& .MuiOutlinedInput-input ': {
        textAlign: 'right',
        width: '100%',
        padding: '8px 6px',
      },
    },
  },
}));

type Props = {
  control: Control<
    {
      charges: ChargeFormEntry[];
    },
    {
      charges: ChargeFormEntry[];
    }
  >;
  hideTax: boolean;
  isOrderTypeNotHUD1: boolean;
};

export const TotalsCard: FC<Props> = ({ control, hideTax, isOrderTypeNotHUD1 }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const charges = useWatch({ control, name: 'charges' });

  const { amount, tax, buyerAtclosing, buyerBefore, sellerAtclosing, sellerBefore, otherTotal } =
    useMemo(() => getTotalRow(charges), [charges]);

  return (
    <Paper className={classes.card}>
      <TitleWithValue title={t('charges:totals')}>
        <Tooltip title={formatMoney(amount)} arrow>
          <Box>
            <NumberField
              disabled
              prefix="$"
              thousandSeparator
              color="secondary"
              className={classes.numberField}
              variant="outlined"
              value={amount}
            />
          </Box>
        </Tooltip>
      </TitleWithValue>

      {!hideTax && (
        <TitleWithValue title="Tax">
          <NumberField disabled prefix="$" thousandSeparator color="secondary" className={classes.numberField} variant="outlined" value={tax} />
        </TitleWithValue>
      )}

      <TitleWithValue
        title={
          isOrderTypeNotHUD1
            ? `${t('charges:paid-by-borrower')} - ${t('charges:at-closing')}`
            : t('charges:paidByBorrower')
        }
      >
        <NumberField
          disabled
          prefix="$"
          thousandSeparator
          color="secondary"
          className={classes.numberField}
          variant="outlined"
          value={buyerAtclosing}
        />
      </TitleWithValue>

      {isOrderTypeNotHUD1 && (
        <TitleWithValue title={`${t('charges:paid-by-borrower')} - ${t('charges:before-closing')}`}>
          <NumberField
            disabled
            prefix="$"
            thousandSeparator
            color="secondary"
            className={classes.numberField}
            variant="outlined"
            value={buyerBefore}
          />
        </TitleWithValue>
      )}

      <TitleWithValue
        title={
          isOrderTypeNotHUD1
            ? `${t('charges:paid-by-seller')} - ${t('charges:at-closing')}`
            : t('charges:paidBySeller')
        }
      >
        <NumberField
          disabled
          prefix="$"
          thousandSeparator
          color="secondary"
          className={classes.numberField}
          variant="outlined"
          value={sellerAtclosing}
        />
      </TitleWithValue>

      {isOrderTypeNotHUD1 && (
        <>
          <TitleWithValue title={`${t('charges:paid-by-seller')} - ${t('charges:before-closing')}`}>
            <NumberField
              disabled
              prefix="$"
              thousandSeparator
              color="secondary"
              className={classes.numberField}
              variant="outlined"
              value={sellerBefore}
            />
          </TitleWithValue>

          <TitleWithValue title={t('charges:by-others')}>
            <NumberField
              disabled
              prefix="$"
              thousandSeparator
              color="secondary"
              className={classes.numberField}
              variant="outlined"
              value={otherTotal}
            />
          </TitleWithValue>
        </>
      )}
    </Paper>
  );
};
